import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { FileUp, LogOut, PackagePlus, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const categories = ["Time-Saving Systems", "Customer Acquisition", "Marketing Toolkits", "Operations Organizers"];
const emptyProduct = { name: "", category: categories[0], summary: "", description: "", price: "", currency: "USD", image_url: "", is_published: false };

export function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [overviewResponse, productsResponse, messagesResponse, ordersResponse] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/products"),
        api.get("/admin/messages"),
        api.get("/admin/orders"),
      ]);
      setOverview(overviewResponse.data);
      setProducts(productsResponse.data);
      setMessages(messagesResponse.data);
      setOrders(ordersResponse.data);
    } catch (error) {
      toast.error(formatApiError(error, "Unable to load the dashboard"));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === undefined) return <div data-testid="admin-loading" className="flex min-h-screen items-center justify-center bg-[#111315] text-white">Checking access…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const createProduct = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/products", { ...form, price: Number(form.price), image_url: form.image_url || null });
      setForm(emptyProduct);
      toast.success("Product added");
      await load();
    } catch (error) {
      toast.error(formatApiError(error, "Product could not be added"));
    } finally {
      setSaving(false);
    }
  };
  const togglePublish = async (product) => {
    try {
      await api.put(`/admin/products/${product.id}`, { is_published: !product.is_published });
      await load();
    } catch (error) {
      toast.error(formatApiError(error, "Product could not be updated"));
    }
  };
  const removeProduct = async (product) => {
    if (!window.confirm(`Remove ${product.name}?`)) return;
    try {
      await api.delete(`/admin/products/${product.id}`);
      toast.success("Product removed");
      await load();
    } catch (error) {
      toast.error(formatApiError(error, "Product could not be removed"));
    }
  };
  const uploadFile = async (product, file) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      await api.post(`/admin/products/${product.id}/file`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Delivery file attached");
      await load();
    } catch (error) {
      toast.error(formatApiError(error, "File could not be uploaded"));
    }
  };
  const markRead = async (message) => {
    await api.patch(`/admin/messages/${message.id}/read`).catch(() => toast.error("Message could not be updated"));
    await load();
  };

  return (
    <Layout>
      <Seo title="Owner dashboard" description="Manage products, contact messages, orders, and digital delivery files." />
      <section className="min-h-screen bg-[#0E1012] px-6 pb-24 pt-36 text-white sm:pt-40">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-500">Owner dashboard</p>
              <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">Store command room.</h1>
            </div>
            <div className="flex gap-3">
              <Button onClick={load} variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="admin-refresh-button"><RefreshCcw className="h-4 w-4" /> Refresh</Button>
              <Button onClick={logout} variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Log out</Button>
            </div>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Products", overview?.products ?? "—"],
              ["Published", overview?.published ?? "—"],
              ["Messages", overview?.messages ?? "—"],
              ["Orders", overview?.orders ?? "—"],
            ].map(([label, value]) => <div key={label} className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-6"><p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</p><p className="mt-3 font-heading text-4xl font-extrabold">{value}</p></div>)}
          </div>

          <div className="mt-10 flex gap-3 overflow-x-auto">
            {["products", "messages", "orders"].map((item) => <button key={item} type="button" onClick={() => setTab(item)} data-testid={`admin-tab-${item}`} className={`rounded-full px-6 py-3 text-sm font-bold capitalize transition-colors ${tab === item ? "bg-amber-600 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{item}</button>)}
          </div>

          {tab === "products" && (
            <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <form onSubmit={createProduct} data-testid="admin-product-form" className="rounded-[2rem] border border-white/10 bg-white/[0.05] p-6 md:p-8">
                <div className="flex items-center gap-3"><PackagePlus className="h-6 w-6 text-amber-500" /><h2 className="font-heading text-2xl font-bold">Add a product</h2></div>
                <div className="mt-7 grid gap-5">
                  <Input required minLength={3} placeholder="Product name" value={form.name} onChange={(event) => updateForm("name", event.target.value)} data-testid="product-name-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                  <select value={form.category} onChange={(event) => updateForm("category", event.target.value)} data-testid="product-category-select" className="h-12 rounded-2xl border border-white/10 bg-[#181A1F] px-4 text-sm text-white">
                    {categories.map((category) => <option key={category}>{category}</option>)}
                  </select>
                  <Input required minLength={10} placeholder="Short summary" value={form.summary} onChange={(event) => updateForm("summary", event.target.value)} data-testid="product-summary-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                  <Textarea required minLength={20} placeholder="Full product description" value={form.description} onChange={(event) => updateForm("description", event.target.value)} data-testid="product-description-input" className="min-h-32 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input required type="number" min="0.01" step="0.01" placeholder="Price" value={form.price} onChange={(event) => updateForm("price", event.target.value)} data-testid="product-price-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                    <Input placeholder="Currency" value={form.currency} onChange={(event) => updateForm("currency", event.target.value.toUpperCase())} data-testid="product-currency-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                  </div>
                  <Input type="url" placeholder="Preview image URL (optional)" value={form.image_url} onChange={(event) => updateForm("image_url", event.target.value)} data-testid="product-image-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
                  <label className="flex items-center gap-3 text-sm text-white/70"><input type="checkbox" checked={form.is_published} onChange={(event) => updateForm("is_published", event.target.checked)} data-testid="product-published-checkbox" className="h-4 w-4 accent-amber-600" /> Publish immediately</label>
                  <Button type="submit" disabled={saving} className="h-12 rounded-full bg-[#D97706] text-white hover:bg-[#B45309]" data-testid="product-create-button">{saving ? "Adding…" : "Add product"}</Button>
                </div>
              </form>

              <div className="space-y-4" data-testid="admin-product-list">
                {products.length === 0 ? (
                  <div className="rounded-[2rem] border border-dashed border-white/15 bg-white/[0.03] p-10 text-center text-white/55">No products yet. Add the first tool with the form.</div>
                ) : products.map((product) => (
                  <article key={product.id} className="rounded-[2rem] border border-white/10 bg-white/[0.05] p-6">
                    <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                      <div>
                        <div className="flex flex-wrap items-center gap-2"><h3 className="font-heading text-xl font-bold">{product.name}</h3><Badge className={product.is_published ? "bg-[#3F5E4D] text-white" : "bg-white/10 text-white/60"}>{product.is_published ? "Published" : "Draft"}</Badge></div>
                        <p className="mt-2 text-sm text-white/55">{product.category} · ${product.price.toFixed(2)}</p>
                        <p className="mt-3 line-clamp-2 text-sm leading-6 text-white/62">{product.summary}</p>
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">{product.file_name ? `File: ${product.file_name}` : "No delivery file attached"}</p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button onClick={() => togglePublish(product)} variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid={`product-publish-toggle-${product.slug}`}>{product.is_published ? "Unpublish" : "Publish"}</Button>
                        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 text-sm font-semibold text-white hover:bg-white/10" data-testid={`product-file-label-${product.slug}`}>
                          <FileUp className="h-4 w-4" /> File
                          <input type="file" className="hidden" onChange={(event) => uploadFile(product, event.target.files?.[0])} data-testid={`product-file-input-${product.slug}`} />
                        </label>
                        <Button onClick={() => removeProduct(product)} variant="outline" className="rounded-full border-red-400/20 bg-red-500/10 text-red-100 hover:bg-red-500/20" data-testid={`product-delete-button-${product.slug}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {tab === "messages" && (
            <div className="mt-8 grid gap-4" data-testid="admin-messages-list">
              {messages.length === 0 ? <div className="rounded-[2rem] border border-dashed border-white/15 bg-white/[0.03] p-10 text-center text-white/55">No contact messages yet.</div> : messages.map((message) => (
                <article key={message.id} className="rounded-[2rem] border border-white/10 bg-white/[0.05] p-6">
                  <div className="flex flex-col justify-between gap-4 md:flex-row">
                    <div>
                      <div className="flex items-center gap-3"><h3 className="font-heading text-xl font-bold">{message.name}</h3>{!message.read && <Badge className="bg-amber-600 text-white">New</Badge>}</div>
                      <p className="mt-1 text-sm text-amber-400">{message.email}{message.business ? ` · ${message.business}` : ""}</p>
                      <p className="mt-4 max-w-3xl text-sm leading-7 text-white/68">{message.message}</p>
                    </div>
                    {!message.read && <Button onClick={() => markRead(message)} variant="outline" className="h-fit rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid={`message-read-button-${message.id}`}>Mark read</Button>}
                  </div>
                </article>
              ))}
            </div>
          )}

          {tab === "orders" && (
            <div className="mt-8 overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.05]" data-testid="admin-orders-list">
              {orders.length === 0 ? <div className="p-10 text-center text-white/55">No orders yet.</div> : orders.map((order) => (
                <div key={order.id} className="grid gap-3 border-b border-white/10 p-6 last:border-b-0 md:grid-cols-4">
                  <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">Product</p><p className="mt-1 font-semibold">{order.product_name}</p></div>
                  <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">Status</p><p className="mt-1 capitalize">{order.status}</p></div>
                  <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">Customer</p><p className="mt-1">{order.customer_email || "Pending"}</p></div>
                  <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/38">Amount</p><p className="mt-1">${order.price.toFixed(2)} {order.currency}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
